import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { prefs } from '../lib/storage.js';
import { getColorScheme, getWebApp, syncChromeColors } from '../telegram/webapp.js';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export interface ThemeValue {
  mode: ThemeMode;
  resolved: ResolvedTheme;
  setMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeValue | null>(null);
const THEME_PREF = 'theme';

function systemTheme(): ResolvedTheme {
  const tg = getColorScheme();
  if (tg) return tg;
  if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
}

export function initialThemeMode(): ThemeMode {
  const stored = prefs.get(THEME_PREF);
  return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system';
}

export function resolveTheme(mode: ThemeMode): ResolvedTheme {
  return mode === 'system' ? systemTheme() : mode;
}

/** Apply the resolved theme to the document element; safe to call pre-render. */
export function applyTheme(resolved: ResolvedTheme): void {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', resolved);
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(initialThemeMode);
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveTheme(mode));

  useEffect(() => {
    const next = resolveTheme(mode);
    setResolved(next);
    applyTheme(next);
    syncChromeColors();
  }, [mode]);

  useEffect(() => {
    if (mode !== 'system') return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const update = () => {
      const next = resolveTheme('system');
      setResolved(next);
      applyTheme(next);
      syncChromeColors();
    };
    media.addEventListener('change', update);
    const app = getWebApp();
    app?.onEvent('themeChanged', update);
    return () => {
      media.removeEventListener('change', update);
      app?.offEvent('themeChanged', update);
    };
  }, [mode]);

  const setMode = useCallback((next: ThemeMode) => {
    prefs.set(THEME_PREF, next);
    setModeState(next);
  }, []);

  const value = useMemo<ThemeValue>(() => ({ mode, resolved, setMode }), [mode, resolved, setMode]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
